import { Route, Routes } from 'react-router-dom';
import { useCurrentUser } from '../modules/auth';
import { formatUserDisplayName } from '../lib/formatUserDisplayName';
import { PageHeader } from '../components/layout/PageHeader';
import { AccountDetail, AccountEdit, AccountsPage, AccountNew, ImportLocations } from '../modules/accounts';
import {
  BulkContactUpload,
  ContactDetail,
  ContactEdit,
  ContactList,
  ContactNew,
} from '../modules/contacts';
import { CampaignDetail, CampaignEdit, CampaignNew, CampaignsPage } from '../modules/campaigns';
import { PlanDetail, PlanEdit, PlanNew, PlansPage } from '../modules/plans';
import {
  SettingsPage,
  UsersPage,
  TaskTemplateDetail,
  TaskTemplateForm,
} from '../modules/settings';
import {
  BuildViewPage,
  GoogleGrid,
  MetaGrid,
  RolloutDetail,
  RolloutList,
  RolloutNew,
  RowEditor,
} from '../modules/rollouts';
import {
  BroadcastCompose,
  BroadcastDetail,
  BroadcastsPage,
  DeletedBroadcastsPage,
} from '../modules/broadcasts';
import { SurveysPage, SurveyCompose, SurveyResultsPage, DeletedSurveysPage } from '../modules/surveys';
import { PreviewSendReport } from '../modules/preview';
import { AddAdToLiveAdSetPage, CloneCampaignPage, ExpressImagesPage, MetaAdsListPage, MetaAdCompose, MetaSetupPage } from '../modules/metaAds';
import {
  OnboardingPage,
  OnboardingCallbackPage,
  BulkOnboardingPage,
  BulkOnboardingCallbackPage,
  GbpCallbackPage,
  GbpOnboardingPage,
  GoogleAdsCallbackPage,
  InstagramCallbackPage,
} from '../modules/onboarding';
import {
  AudiencePage,
  BackfillsPage,
  CoveragePage,
  CoverageGridPage,
  SourceHealthPage,
  ReportingPage,
  SocialReportingPage,
  SyncHealthPage,
} from '../modules/reporting';
import { GoogleAdsPage } from '../modules/googleAds';
import {
  AdvertisingHomePage,
  GoogleHomePage,
  MetaHomePage,
} from '../modules/advertisingHome';
import { ConnectionHealthPage } from '../modules/connectionHealth';
import { InstagramBackfillPage } from '../modules/instagramBackfill';
import { CrmOnboardingPage } from '../modules/crmOnboarding';
import { Ga4ConnectionsPage } from '../modules/ga4Connections';
import { AccountAnalyticsPage } from '../modules/accountAnalytics';
import { XeroConnectionsPage, XeroMappingPage, XeroAllocationsPage } from '../modules/xero';
import { AllLeadsPage } from '../modules/speedToLead';
import { AllCallsPage, KyckConnectOverviewPage, KyckConnectReportsPage, NumbersPage, TestLabPage } from '../modules/kyckConnect';
import { legacyRedirectRoutes } from './LegacyRedirects';
import { CommunityPage } from '../modules/community';
import { BookingCalendarsPage, BookingCalendarPage } from '../modules/appointments';
import { HostedReportsPage } from '../modules/hostedReports';
import { PortalLibraryPage } from '../modules/portalLibrary';
import { PortalAssetsToolPage } from '../modules/portalAssets';
import { InspirationToolPage } from '../modules/portalInspiration';
import { BotSettingsPage, BoothDashboardPage, LeadBotPage } from '../modules/leadBot';
import { LeadLogPage, LeadRouteEditPage, LeadRoutesPage } from '../modules/leadRelay';
import { ClientReportPreview } from '../modules/clientPortal';
import { AmDashboardPage, RoleHome } from '../modules/amDashboard';
import {
  PublishingCalendarPage,
  PublishingDraftsPage,
  ComposerPage,
  MultiComposerPage,
} from '../modules/publishing';

function Home() {
  const user = useCurrentUser();
  return (
    <div>
      <PageHeader
        title="Dashboard"
        description={`Welcome, ${formatUserDisplayName(user, { full: true })}.`}
      />
      <p className="max-w-2xl text-sm text-slate-600">
        This page is a placeholder — the active-campaign list and progress bars arrive in P4.
      </p>
    </div>
  );
}

function NotFound() {
  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-semibold mb-2">Not found</h1>
      <p className="text-slate-600">The page you're looking for doesn't exist.</p>
    </div>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <RoleHome>
            <Home />
          </RoleHome>
        }
      />
      <Route path="/dashboard/am" element={<AmDashboardPage />} />
      <Route path="/accounts" element={<AccountsPage />} />
      <Route path="/accounts/new" element={<AccountNew />} />
      <Route path="/accounts/:id" element={<AccountDetail />} />
      <Route path="/accounts/:id/edit" element={<AccountEdit />} />
      <Route path="/accounts/:id/import" element={<ImportLocations />} />
      <Route path="/contacts" element={<ContactList />} />
      <Route path="/contacts/bulk-upload" element={<BulkContactUpload />} />
      <Route path="/contacts/new" element={<ContactNew />} />
      <Route path="/contacts/:id" element={<ContactDetail />} />
      <Route path="/contacts/:id/edit" element={<ContactEdit />} />
      <Route path="/campaigns" element={<CampaignsPage />} />
      <Route path="/campaigns/new" element={<CampaignNew />} />
      <Route path="/campaigns/:id" element={<CampaignDetail />} />
      <Route path="/campaigns/:id/edit" element={<CampaignEdit />} />
      <Route path="/plans" element={<PlansPage />} />
      <Route path="/plans/new" element={<PlanNew />} />
      <Route path="/plans/:id" element={<PlanDetail />} />
      <Route path="/plans/:id/edit" element={<PlanEdit />} />
      {/* Round 2, item 7 — staff "view as client". Inside the STAFF shell and
          behind the staff guard; its data comes from /api/client-preview/*. */}
      <Route path="/tools/client-report" element={<ClientReportPreview />} />
      <Route path="/settings" element={<SettingsPage />} />
      <Route
        path="/settings/templates/new"
        element={<TaskTemplateForm />}
      />
      <Route
        path="/settings/templates/:id"
        element={<TaskTemplateDetail />}
      />
      <Route path="/rollouts" element={<RolloutList />} />
      <Route path="/rollouts/new" element={<RolloutNew />} />
      <Route path="/rollouts/:id" element={<RolloutDetail />} />
      <Route path="/rollouts/:id/grid/meta" element={<MetaGrid />} />
      <Route
        path="/rollouts/:id/grid/meta/:rowId/build"
        element={<BuildViewPage platform="meta" />}
      />
      <Route path="/rollouts/:id/grid/google" element={<GoogleGrid />} />
      <Route
        path="/rollouts/:id/grid/google/:rowId/build"
        element={<BuildViewPage platform="google" />}
      />
      <Route path="/rollouts/:id/rows/:rowId" element={<RowEditor />} />
      <Route path="/broadcasts" element={<BroadcastsPage />} />
      <Route path="/broadcasts/deleted" element={<DeletedBroadcastsPage />} />
      <Route path="/broadcasts/new" element={<BroadcastCompose />} />
      <Route path="/broadcasts/:id/edit" element={<BroadcastCompose />} />
      <Route path="/broadcasts/:id" element={<BroadcastDetail />} />
      <Route path="/surveys" element={<SurveysPage />} />
      <Route path="/surveys/deleted" element={<DeletedSurveysPage />} />
      <Route path="/surveys/new" element={<SurveyCompose />} />
      <Route path="/surveys/:id/edit" element={<SurveyCompose />} />
      <Route path="/surveys/:id" element={<SurveyResultsPage />} />
      <Route path="/advertising/home" element={<AdvertisingHomePage />} />
      {/* The single-channel specialist boards. ⚠️ NO `end` flag is needed on
          their nav entries: React Router matches whole path segments, so
          /advertising/meta does not match /advertising/meta-home — the
          existing /advertising/meta-setup is the precedent that proves it. */}
      <Route path="/advertising/meta-home" element={<MetaHomePage />} />
      <Route path="/advertising/google-home" element={<GoogleHomePage />} />
      <Route path="/advertising/meta" element={<MetaAdsListPage />} />
      <Route path="/advertising/meta/compose/:draftId" element={<MetaAdCompose />} />
      <Route path="/advertising/meta/add-to-live" element={<AddAdToLiveAdSetPage />} />
      <Route path="/advertising/meta/express-images" element={<ExpressImagesPage />} />
      <Route path="/advertising/meta/clone" element={<CloneCampaignPage />} />
      <Route path="/advertising/meta-setup" element={<MetaSetupPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/onboarding/callback" element={<OnboardingCallbackPage />} />
      <Route path="/onboarding/gbp/callback" element={<GbpCallbackPage />} />
      <Route path="/onboarding/instagram/callback" element={<InstagramCallbackPage />} />
      <Route path="/onboarding/google-ads/callback" element={<GoogleAdsCallbackPage />} />
      <Route path="/onboarding/gbp-agency" element={<GbpOnboardingPage />} />
      <Route path="/onboarding/bulk" element={<BulkOnboardingPage />} />
      <Route path="/onboarding/bulk/callback" element={<BulkOnboardingCallbackPage />} />
      <Route path="/reporting" element={<ReportingPage />} />
      <Route path="/reporting/google-ads" element={<GoogleAdsPage />} />
      <Route path="/reporting/audience" element={<AudiencePage />} />
      <Route path="/reporting/social" element={<SocialReportingPage />} />
      <Route path="/reporting/backfills" element={<BackfillsPage />} />
      <Route path="/reporting/coverage" element={<CoveragePage />} />
      <Route path="/reporting/health" element={<SourceHealthPage />} />
      <Route path="/instagram-backfill" element={<InstagramBackfillPage />} />
      <Route path="/crm-onboarding" element={<CrmOnboardingPage />} />
      <Route path="/ga4-connections" element={<Ga4ConnectionsPage />} />
      <Route path="/reporting/grid" element={<CoverageGridPage />} />
      <Route path="/reporting/sync-health" element={<SyncHealthPage />} />
      <Route path="/community" element={<CommunityPage />} />
      {/* KYCKCONNECT (redesign Phase 1): everything about lead handling lives under /kyckconnect. The old URLs
          (/lead-bot, /lead-relay/*, /speed-to-lead/leads, /settings/speed-to-lead, /business-profile/:id) redirect here,
          with the query string, via legacyRedirectRoutes below. */}
      <Route path="/kyckconnect" element={<KyckConnectOverviewPage />} />
      <Route path="/kyckconnect/leads" element={<AllLeadsPage />} />
      {/* The approval queue (and the conversation viewer). ⚠️ Draft mode: every reply the assistant writes waits here for a person. */}
      <Route path="/kyckconnect/approvals" element={<LeadBotPage />} />
      <Route path="/kyckconnect/calls" element={<AllCallsPage />} />
      <Route path="/kyckconnect/numbers" element={<NumbersPage />} />
      <Route path="/kyckconnect/test-lab" element={<TestLabPage />} />
      <Route path="/kyckconnect/reports" element={<KyckConnectReportsPage />} />
      <Route path="/kyckconnect/bot-settings" element={<BotSettingsPage />} />
      {/* The conference booth big screen: covers the shell, real counts only. ⚠️ DOES NOT MOVE (conference links). */}
      <Route path="/lead-bot/live/:botId" element={<BoothDashboardPage />} />
      {/* Booking calendars — the bookable-calendar model, its rules and the
          connected staff. ⚠️ Its own surface rather than a page inside the lead
          bot: the CRM and web chat will both want booking. */}
      <Route path="/booking-calendars" element={<BookingCalendarsPage />} />
      <Route path="/booking-calendars/:id" element={<BookingCalendarPage />} />
      {/* Meta Lead → VirtualYard relay. ONE path serves create and edit (`new`
          is the id on create), so a create keeps its component — and its saved
          warnings and test-lead prompt — when it moves to the route's own URL. */}
      <Route path="/kyckconnect/lead-relay/log" element={<LeadLogPage />} />
      <Route path="/kyckconnect/lead-relay/routes" element={<LeadRoutesPage />} />
      <Route path="/kyckconnect/lead-relay/routes/:id" element={<LeadRouteEditPage />} />
      <Route path="/publishing" element={<PublishingCalendarPage />} />
      {/* Before /:id/edit — "drafts" and "new" are literal segments, not ids. */}
      <Route path="/publishing/drafts" element={<PublishingDraftsPage />} />
      <Route path="/publishing/new" element={<ComposerPage />} />
      <Route path="/publishing/multi" element={<MultiComposerPage />} />
      <Route path="/publishing/:id/edit" element={<ComposerPage />} />
      {/* Tools -> HTML Hosting. The /tools/* prefix is deliberate: the sidebar
          group is built to take more tools, and a shared prefix keeps their
          routes (and the group's route-ownership resolution) together. */}
      <Route path="/tools/html-hosting" element={<HostedReportsPage />} />
      <Route path="/tools/portal-library" element={<PortalLibraryPage />} />
      <Route path="/tools/portal-assets" element={<PortalAssetsToolPage />} />
      <Route path="/tools/inspiration" element={<InspirationToolPage />} />
      <Route path="/connection-health" element={<ConnectionHealthPage />} />
      <Route path="/account-analytics" element={<AccountAnalyticsPage />} />
      <Route path="/settings/users" element={<UsersPage />} />
      <Route path="/settings/xero" element={<XeroConnectionsPage />} />
      <Route path="/settings/xero/mapping" element={<XeroMappingPage />} />
      <Route path="/settings/xero/allocations" element={<XeroAllocationsPage />} />
      <Route path="/preview-sends/:batchId" element={<PreviewSendReport />} />
      {legacyRedirectRoutes}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
